import { useId, type ComponentPropsWithRef } from 'react';
import { controlClassName, describedByOf, Field, type FieldTexts } from '../Field/Field.tsx';
import { DateInput } from './DateInput.tsx';
import styles from './Input.module.css';

export type InputProps = ComponentPropsWithRef<'input'> & FieldTexts & { suffix?: string | undefined };

type ControlProps = Omit<InputProps, 'helperText' | 'hideLabel'> & { describedBy: string | undefined };

const InputControl = ({ label, errorText, suffix, describedBy, type, className, ...rest }: ControlProps) => {
  const aria = { 'aria-invalid': errorText ? true : undefined, 'aria-describedby': describedBy };
  if (type === 'date')
    return <DateInput invalid={errorText !== undefined} className={className} {...aria} {...rest} />;
  if (suffix === undefined)
    return <input type={type} className={controlClassName(errorText, className)} {...aria} {...rest} />;
  return (
    <span className={[styles['affixed'], errorText ? styles['invalid'] : ''].join(' ')}>
      <input
        type={type}
        className={[styles['affixedInput'], className ?? ''].join(' ')}
        aria-label={`${label} (${suffix})`}
        {...aria}
        {...rest}
      />
      <span className={styles['suffix']} aria-hidden="true">
        {suffix}
      </span>
    </span>
  );
};

export const Input = ({ label, helperText, errorText, hideLabel, required, id, ...rest }: InputProps) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  return (
    <Field
      id={inputId}
      label={label}
      helperText={helperText}
      errorText={errorText}
      hideLabel={hideLabel}
      required={required}
    >
      <InputControl
        id={inputId}
        label={label}
        errorText={errorText}
        required={required}
        describedBy={describedByOf(inputId, { helperText, errorText })}
        {...rest}
      />
    </Field>
  );
};
